/**
 * Village / illager family models (vanilla geometry, sheets read on their own
 * pixel grid):
 *  - villager: VillagerModel — 8x10x8 head with the 2x4x2 nose sticking out
 *    front, 8x12x6 body with the robe overlay (0,38), folded 4x8x4 arms, and
 *    the classic idle arm pose (pivot (0,3,-1), rot.x = -0.75).
 *  - zombie villager: the same head/body, arms split so they can take the
 *    zombie forward pose, no folded-arm bundle.
 *  - pillager / vindicator: IllagerModel — same head/nose/robe, but 4x12x4
 *    arms at (40,46) so the crossbow / axe can hang off the right hand.
 *  - iron golem: IronGolemModel (128x128 sheet) — 18x12x11 body with the
 *    (0,70) skirt, 30px arms pivoting from the body centre, 6x16x5 legs; its
 *    feet sit at y = 24 px, so the head tops out at ~43 px (2.7 blocks).
 *
 * A sheet that is missing from public/mc/entity/ swaps in a procedural
 * stand-in, so a half-extracted asset dump still draws a mob instead of a
 * black silhouette.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { MobView } from '../../game/types';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat } from './core';
import type { QuadPose } from './mobs';
import { applyHandheld, itemSpriteMesh } from './extras';

export interface VillageAnim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  /** Head part, when the renderer needs it (baby offsets). */
  head?: THREE.Object3D;
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

const PI = Math.PI;

const cube = (
  tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false,
): CubeDef => ({ tex, from, size, inflate, mirror });

/** buildPart bound to one sheet's pixel size + material (parent defaults to `parts`). */
type PartFn = (def: Parameters<typeof buildPart>[0], parent?: THREE.Object3D) => THREE.Group;

function partMaker(parts: THREE.Group, tw: number, th: number, mat: THREE.Material): PartFn {
  return (def, parent) => {
    const g = buildPart(def, tw, th, mat);
    (parent ?? parts).add(g);
    return g;
  };
}

// --- texture -------------------------------------------------------------------

const FALLBACK = new Map<number, THREE.Texture>();

/** Tinted 2-tone checker, the stand-in for a sheet that is not in the dump. */
function fallbackTexture(tint: number): THREE.Texture {
  let t = FALLBACK.get(tint);
  if (t) return t;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 64;
  const cx = cv.getContext('2d')!;
  const c = new THREE.Color(tint);
  for (let y = 0; y < 64; y++) {
    for (let x = 0; x < 64; x++) {
      const k = (((x >> 2) + (y >> 2)) & 1) ? 1 : 0.7;
      cx.fillStyle = `rgb(${Math.round(c.r * 255 * k)},${Math.round(c.g * 255 * k)},${Math.round(c.b * 255 * k)})`;
      cx.fillRect(x, y, 1, 1);
    }
  }
  t = new THREE.CanvasTexture(cv);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.flipY = false;
  t.colorSpace = THREE.SRGBColorSpace;
  FALLBACK.set(tint, t);
  return t;
}

/** Sheet material; a failed load swaps the map for a procedural stand-in. */
function sheetMat(env: EnvUniforms, path: string, tint: number): EntityMat {
  const mat = entityMaterial(env, sheetTexture(path));
  const probe = new Image();
  probe.onerror = () => { mat.uniforms.uMap.value = fallbackTexture(tint); };
  probe.src = `mc/${path}.png`;
  return mat;
}


// --- layered villager textures -------------------------------------------------

const VTYPES = ['plains', 'desert', 'savanna', 'taiga', 'snow', 'jungle', 'swamp'] as const;
const VPROFS = [
  'none', 'nitwit', 'farmer', 'librarian', 'fletcher', 'fisherman', 'toolsmith', 'weaponsmith', 'armorer', 'butcher', 'mason',
  'cleric', 'leatherworker', 'cartographer', 'shepherd',
] as const;
const VBADGES = ['stone', 'iron', 'gold', 'emerald', 'diamond'] as const;

const IMGS = new Map<string, HTMLImageElement | null | 'loading'>();
const COMPOSED = new Map<string, THREE.CanvasTexture>();

/** Layer list vanilla draws, in order: base skin, biome type, profession, level badge. */
export function villagerLayers(zombie: boolean, baby: boolean, type: number, prof: number, level: number): string[] {
  const root = zombie ? 'entity/zombie_villager' : 'entity/villager';
  const t = VTYPES[Math.min(VTYPES.length - 1, Math.max(0, type))];
  if (baby) return [`${root}/${zombie ? 'zombie_villager_baby' : 'villager_baby'}`, `${root}/baby/${t}`];
  const out = [`${root}/${zombie ? 'zombie_villager' : 'villager'}`, `${root}/type/${t}`];
  const p = VPROFS[prof] ?? 'none';
  if (p !== 'none') {
    out.push(`${root}/profession/${p}`);
    if (p !== 'nitwit' && !zombie) out.push(`entity/villager/profession_level/${VBADGES[Math.min(4, Math.max(0, level - 1))]}`);
  }
  return out;
}

/** Composite sheet for a layer list (cached); redraws as the PNGs arrive. */
export function composedTexture(layers: readonly string[], tint: number): THREE.CanvasTexture {
  const key = layers.join('+');
  let t = COMPOSED.get(key);
  if (t) return t;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 64;
  const cx = cv.getContext('2d')!;
  t = new THREE.CanvasTexture(cv);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.flipY = false;
  t.colorSpace = THREE.SRGBColorSpace;
  COMPOSED.set(key, t);
  const tex = t;
  const draw = (): void => {
    cx.clearRect(0, 0, 64, 64);
    const base = IMGS.get(layers[0]);
    if (!base || base === 'loading') { cx.drawImage(fallbackTexture(tint).image as HTMLCanvasElement, 0, 0); }
    for (const l of layers) {
      const im = IMGS.get(l);
      if (im && im !== 'loading') cx.drawImage(im, 0, 0);
    }
    tex.needsUpdate = true;
  };
  let pending = 0;
  for (const l of layers) {
    const have = IMGS.get(l);
    if (have !== undefined && have !== 'loading') continue;
    pending++;
    const im = new Image();
    im.onload = () => { IMGS.set(l, im); if (--pending <= 0) draw(); };
    im.onerror = () => { IMGS.set(l, null); if (--pending <= 0) draw(); };
    IMGS.set(l, 'loading');
    im.src = `mc/${l}.png`;
  }
  if (pending === 0) draw();
  return t;
}

// --- villager ------------------------------------------------------------------

/**
 * Vanilla arm-frame hand tip (Humanoid's rightHand transform): rotate the arm
 * frame by Rx(-90)*Ry(180), then translate (-1/16, .125, -.625) in it. Scale 16
 * turns the pixel-space arm frame back into block units.
 */
const HAND_Q = new THREE.Quaternion()
  .setFromEuler(new THREE.Euler(-PI / 2, 0, 0))
  .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, PI, 0)));

function handAnchor(arm: THREE.Group): THREE.Object3D {
  const g = new THREE.Group();
  g.rotation.order = 'XYZ';
  g.quaternion.copy(HAND_Q);
  g.position.copy(new THREE.Vector3(-1, 2, -10).applyQuaternion(HAND_Q));
  g.scale.set(16, 16, 16);
  arm.add(g);
  return g;
}

interface VillageRig {
  parts: THREE.Group; mats: EntityMat[];
  head: THREE.Group; arms: THREE.Group; armL: THREE.Group; armR: THREE.Group;
  legR: THREE.Group; legL: THREE.Group;
}

/** head + nose + body + robe, shared by every villager-shaped model. */
function villageTorso(P: PartFn): THREE.Group {
  const head = P({
    cubes: [
      cube([0, 0], [-4, -10, -4], [8, 10, 8]), cube([24, 0], [-1, -3, -6], [2, 4, 2]),
      cube([32, 0], [-4, -10, -4], [8, 10, 8], 0.51), // hat layer (profession / biome headwear)
    ],
  });
  // hat rim: the 16x16x1 brim lying flat at the middle of the head (vanilla hat_rim, rot.x = -90)
  P({ cubes: [cube([30, 47], [-8, -8, -6], [16, 16, 1])], rot: [-PI / 2, 0, 0] }, head);
  P({
    cubes: [
      cube([16, 20], [-4, 0, -3], [8, 12, 6]),
      cube([0, 38], [-4, 0, -3], [8, 18, 6], 0.5), // robe / apron overlay
    ],
  });
  return head;
}

function villagerRig(env: EnvUniforms, zombie: boolean): VillageRig {
  const mat = entityMaterial(env, composedTexture(villagerLayers(zombie, false, 0, 0, 1), zombie ? 0x4f7a4a : 0x9c6b4a));
  const parts = new THREE.Group();
  const P = partMaker(parts, 64, 64, mat);
  const head = villageTorso(P);
  // folded arms (villager) or one group per arm (zombie villager: 4x12x4 arms)
  const arms = P({ cubes: zombie ? [] : [
    cube([44, 22], [-8, -2, -2], [4, 8, 4]),
    cube([44, 22], [4, -2, -2], [4, 8, 4], 0, true),
    cube([40, 38], [-4, 2, -2], [8, 4, 4]),
  ] });
  const armR = zombie ? P({ cubes: [cube([44, 22], [-3, -2, -2], [4, 12, 4])], pos: [-5, 2, 0] }) : arms;
  const armL = zombie ? P({ cubes: [cube([44, 22], [-1, -2, -2], [4, 12, 4], 0, true)], pos: [5, 2, 0] }) : arms;
  const legR = P({ cubes: [cube([0, 22], [-2, 0, -2], [4, 12, 4])], pos: [-2, 12, 0] });
  const legL = P({ cubes: [cube([0, 22], [-2, 0, -2], [4, 12, 4], 0, true)], pos: [2, 12, 0] });
  return { parts, mats: [mat], head, arms, armL, armR, legR, legL };
}

/**
 * Villager / zombie villager (VillagerModel). The villager keeps its arms
 * folded; the zombie villager takes the zombie arms-forward pose. The sheet is
 * the vanilla layer stack for the villager's biome type / profession / level
 * (packed into MobView.color: type | profession << 3 | level << 7 | asleep << 10).
 */
export function villagerAnim(env: EnvUniforms, zombie: boolean): VillageAnim {
  const r = villagerRig(env, zombie);
  const { head, arms, armL, armR, legR, legL, parts, mats } = r;
  const mat = mats[0];
  let lastKey = -1;
  return {
    parts, mats, head,
    animate(v, q, headY, headX, age) {
      const c = v.color | 0;
      const key = c & 0x3ff; // babies are their own model (babyModels.ts)
      if (key !== lastKey) {
        lastKey = key;
        mat.uniforms.uMap.value = composedTexture(
          villagerLayers(zombie, false, c & 7, (c >> 3) & 15, (c >> 7) & 7), zombie ? 0x4f7a4a : 0x9c6b4a);
      }
      const asleep = (c & 1024) !== 0;
      head.rotation.set(asleep ? 0 : headX + shakeNod(v, 'x'), asleep ? 0 : headY + shakeNod(v, 'y'), 0);
      const s = q.limbSwing * 0.6662, a = asleep ? 0 : q.limbAmt;
      legR.rotation.x = Math.cos(s) * 1.4 * a * 0.5;
      legL.rotation.x = Math.cos(s + PI) * 1.4 * a * 0.5;
      if (!zombie) {
        // VillagerModel.setRotationAngles: arms folded across the chest
        arms.position.set(0, 3, -1);
        arms.rotation.x = -0.75;
        arms.rotation.z = 0;
        void age;
        return;
      }
      // zombie arms: forward, with the vanilla attack zigzag on top
      const t = v.attackAnim;
      const f = Math.sin(t * PI), f1 = Math.sin((1 - (1 - t) * (1 - t)) * PI);
      const base = -PI / 1.5 + Math.cos(s) * 0.2 * a;
      armR.rotation.x = base + f * 1.2 - f1 * 0.4;
      armL.rotation.x = base + f * 1.2 - f1 * 0.4;
      armR.rotation.y = -(0.1 - f * 0.6);
      armL.rotation.y = 0.1 - f * 0.6;
    },
    dispose() { mat.dispose(); },
  };
}

/** Head shake (no trade / refuse): MobView.swell carries shake ticks / 20; vanilla sways the head +-0.3 rad. */
function shakeNod(v: MobView, axis: 'x' | 'y'): number {
  if (v.swell <= 0 || axis === 'x') return 0;
  return Math.sin(v.swell * 20 * 1.2) * 0.45;
}

// --- illagers ------------------------------------------------------------------

/**
 * Pillager / vindicator (IllagerModel): villager head with the nose, robe and
 * 4x12x4 arms, holding `item` (crossbow_standby / iron_axe) in the right hand.
 */
export function illagerAnim(env: EnvUniforms, sheet: string, item: string, tint: number): VillageAnim {
  const mat = sheetMat(env, sheet, tint);
  const parts = new THREE.Group();
  const P = partMaker(parts, 64, 64, mat);
  const head = villageTorso(P);
  const armR = P({ cubes: [cube([40, 46], [-3, -2, -2], [4, 12, 4])], pos: [-5, 2, 0] });
  const armL = P({ cubes: [cube([40, 46], [-1, -2, -2], [4, 12, 4], 0, true)], pos: [5, 2, 0] });
  const legR = P({ cubes: [cube([0, 22], [-2, 0, -2], [4, 12, 4])], pos: [-2, 12, 0] });
  const legL = P({ cubes: [cube([0, 22], [-2, 0, -2], [4, 12, 4], 0, true)], pos: [2, 12, 0] });
  const sprite = itemSpriteMesh(env, item);
  const crossbow = item.startsWith('crossbow');
  applyHandheld(sprite.mesh, crossbow ? 'crossbow' : 'handheld');
  handAnchor(armR).add(sprite.mesh);
  const mats = [mat, sprite.mat];
  return {
    parts, mats,
    animate(v, q, headY, headX, age) {
      head.rotation.set(headX, headY, 0);
      const s = q.limbSwing * 0.6662, a = q.limbAmt;
      legR.rotation.x = Math.cos(s) * 1.4 * a * 0.5;
      legL.rotation.x = Math.cos(s + PI) * 1.4 * a * 0.5;
      // NEUTRAL pose: arms swing with the walk, with the vanilla idle sway
      armR.rotation.x = Math.cos(s + PI) * a + Math.sin(age * 0.067) * 0.05;
      armL.rotation.x = Math.cos(s) * a - Math.sin(age * 0.067) * 0.05;
      armR.rotation.z = Math.cos(age * 0.09) * 0.05 + 0.05;
      armL.rotation.z = -Math.cos(age * 0.09) * 0.05 - 0.05;
      // pillager: the crossbow is always raised (ArmPose.CROSSBOW_HOLD, AnimationUtils.animateCrossbowHold)
      if (crossbow) {
        armR.rotation.set(-PI / 2 + headX + 0.1, -0.3 + headY, 0);
        armL.rotation.set(-1.5 + headX, 0.6 + headY, 0);
        return;
      }
      // ATTACKING: the arm holding the weapon chops
      const t = v.attackAnim;
      if (t > 0) {
        const f = Math.sin(t * PI);
        const f1 = Math.sin((1 - (1 - t) * (1 - t)) * PI);
        armR.rotation.x += -1.8849558 + f * 2.2 - f1 * 0.4;
        armR.rotation.y = 0.15707964;
      } else {
        armR.rotation.y = 0;
      }
    },
    dispose() { for (const m of mats) m.dispose(); },
  };
}

// --- iron golem -----------------------------------------------------------------

/** IronGolemModel.setLivingAnimations triangle wave (period in ticks). */
const triangleWave = (x: number, period: number): number =>
  (Math.abs((x % period) - period * 0.5) - period * 0.25) / (period * 0.25);

/** Iron golem (128x128 sheet): 2.7 blocks tall, arms swinging from the body centre. */
export function ironGolemAnim(env: EnvUniforms): VillageAnim {
  const mat = sheetMat(env, 'entity/iron_golem/iron_golem', 0xb4b4b0);
  const parts = new THREE.Group();
  const P = partMaker(parts, 128, 128, mat);
  const head = P({
    cubes: [cube([0, 0], [-4, -12, -5.5], [8, 10, 8]), cube([24, 0], [-1, -5, -7.5], [2, 4, 2])],
    pos: [0, -7, -2],
  });
  P({
    cubes: [cube([0, 40], [-9, -2, -6], [18, 12, 11]), cube([0, 70], [-4.5, 10, -3], [9, 5, 6], 0.5)],
    pos: [0, -7, 0],
  });
  const armR = P({ cubes: [cube([60, 21], [-13, -2.5, -3], [4, 30, 6])], pos: [0, -7, 0] });
  const armL = P({ cubes: [cube([60, 58], [9, -2.5, -3], [4, 30, 6])], pos: [0, -7, 0] });
  const legNeg = P({ cubes: [cube([37, 0], [-3.5, -3, -3], [6, 16, 5])], pos: [-4, 11, 0] });
  const legPos = P({ cubes: [cube([60, 0], [-3.5, -3, -3], [6, 16, 5], 0, true)], pos: [5, 11, 0] });
  return {
    parts, mats: [mat],
    animate(v, q, headY, headX) {
      head.rotation.set(headX, headY, 0);
      const amt = q.limbAmt;
      const tw = triangleWave(q.limbSwing, 13);
      legNeg.rotation.x = -1.5 * tw * amt;
      legPos.rotation.x = 1.5 * tw * amt;
      if (v.attackAnim > 0) {
        // iron golem smash: both arms sweep up and over
        const at = triangleWave((1 - v.attackAnim) * 10, 10);
        armR.rotation.x = armL.rotation.x = -2 + 1.5 * at;
      } else {
        armR.rotation.x = (-0.2 + 1.5 * tw) * amt;
        armL.rotation.x = (-0.2 - 1.5 * tw) * amt;
      }
    },
    dispose() { mat.dispose(); },
  };
}
